import FormParticipantDataAccordion from '@/components/common/forms/form-participant-data-accordion/FormParticipantDataAccordion';
import FormParticipantData from '@/components/common/forms/form-participant-data/FormParticipantData';
import type { TForm } from '@/components/common/forms/form-participant-data/FormParticipantData.types';
import { RegisteredUserModal } from '@/components/common/modals/registered-user-modal/RegisteredUserModal';
import NewBookingSummaryModal from '@/components/modals/new-booking-summary-modal/NewBookingSummaryModal';
import useCreateSale from '@/hooks/queries/sale/useCreateSale';
import useGetUser from '@/hooks/queries/user/useGetUser';
import { useRootStore } from '@/providers/root-store-provider';
import api from '@/services/api/api';
import type { StepService } from '@/stores/slices/step-service/step-services.types';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { z } from 'zod';
import type ParticipantsDataStepProp from './ParticipantsDataStep.types';
import { Skeleton } from '@/components/ui/skeleton';
import FormParticipantDataVp from '@/components/common/forms/form-participant-data-vp/FormParticipantDataVp';
import { getAgeRange, VEPCodes } from '@/utils/utils';

const ParticipantsDataStep: React.FC<ParticipantsDataStepProp> = ({
  organization
}) => {
  const [isOpenAlertDialog, setIsOpenAlertDialog] = useState(false);
  const stepServicesData: StepService | null = useRootStore(
    (store) => store.stepServicesData
  );
  const loadStepServicesData = useRootStore(
    (store) => store.loadStepServicesData
  );

  const featureFlags = process.env.NEXT_PUBLIC_FEATURE_FLAG
    ? JSON.parse(process.env.NEXT_PUBLIC_FEATURE_FLAG)
    : {};

  // get user data, if is logged
  const { user } = useUser();
  const { data: userData, isPending: isPendingUserData } = useGetUser(
    user?.email
  );
  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  // check if user already exsist
  const emailUser = useRef('');
  const emailHint = emailUser.current.replace(/\+/g, '%2B');
  const {
    data: isEmailValid,
    isSuccess,
    refetch: checkUserEmail,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['checkUser'],
    queryFn: () => api.user.userExistsByEmail(emailUser.current),
    enabled: false
  });

  // post
  const { mutate, isPending } = useCreateSale();

  const onSubmit = (data: z.infer<TForm>) => {
    // parse birthdate to dateISOString
    const participantsData = data.participants.map((participant) => {
      const { id, ...rest } = participant;
      if (id) return id;
      return {
        ...rest,
        birthdate: participant.birthdate.toISOString()
      };
    });

    const userParticipantData = {
      ...data.user,
      birthdate: data.user.birthdate ? data.user.birthdate.toISOString() : '', // parte date to ISO string
      isOwnUser: true // add value to indicate de user is participant
    };

    delete (userParticipantData as { userIsParticipant?: boolean })
      .userIsParticipant; // the end-point does not support this value

    const consumers = data.user.userIsParticipant
      ? [userParticipantData, ...participantsData]
      : participantsData;

    loadStepServicesData({
      email: data.user.email,
      phone: data.user?.phone ? data.user?.phone : undefined,
      firstName: data.user?.firstName,
      lastName: data.user?.lastName,
      identification: data?.user?.identification,
      consumers,
      paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440', //payment redsys
      acceptedTos: data.acceptedTos
    });

    setOpenSummaryModal(true);
  };

  const onSubmitAdditionalData = (data: any) => {
    const group = getAgeRange(data.user.birthdate);
    const { holidayDateDetails, ...restUser } = data.user;
    const { user, ...restData } = data;

    const consumers = [
      {
        ...restUser,
        group: `group-${group}`,
        holidayDate: data.user.holidayDate ? holidayDateDetails : '',
        ...restData,
        user: {
          ...restUser,
          holidayDate: data.user.holidayDate ? holidayDateDetails : ''
        }
      }
    ];

    const userData = data.parents[0];

    const submitValue = {
      email: userData?.email,
      phone: userData?.phone ? userData?.phone : undefined,
      firstName: userData?.firstName,
      lastName: userData?.lastName,
      identification: userData?.identification,
      consumers,
      paymentType: 'ef75aed8-40fb-489f-963c-11722aa12440',
      service: (stepServicesData as StepService).service?.id
    };

    mutate(submitValue);
  };

  useEffect(() => {
    if (isSuccess && isEmailValid && !user?.email) setIsOpenAlertDialog(true);
  }, [dataUpdatedAt]);

  const renderForm = () => {
    if (user?.email ? isPendingUserData : false)
      return (
        <div>
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 mt-1 w-full" />
          <Skeleton className="h-20 mt-5 w-full" />
          <Skeleton className="h-8 mt-5 w-full" />
        </div>
      );

    if (VEPCodes.includes(organization.code)) {
      return (
        <FormParticipantDataVp
          user={userData}
          organization={organization}
          emailRef={emailUser}
          checkUserEmail={checkUserEmail}
          isPending={Boolean(
            isPending || (isSuccess && isEmailValid && !user?.email)
          )}
          onSubmit={onSubmitAdditionalData}
        />
      );
    }

    if (featureFlags?.formParticipants === 'accordion')
      return (
        <FormParticipantDataAccordion
          user={userData}
          organization={organization}
          emailRef={emailUser}
          checkUserEmail={checkUserEmail}
          isPending={Boolean(
            isPending || (isSuccess && isEmailValid && !user?.email)
          )}
          onSubmit={onSubmit}
        />
      );

    return (
      <FormParticipantData
        user={userData}
        organization={organization}
        emailRef={emailUser}
        checkUserEmail={checkUserEmail}
        isPending={Boolean(
          isPending || (isSuccess && isEmailValid && !user?.email)
        )}
        onSubmit={onSubmit}
      />
    );
  };

  return (
    <>
      {openSummaryModal && (
        <NewBookingSummaryModal
          user={userData}
          open={openSummaryModal}
          onOpenChange={setOpenSummaryModal}
          organization={organization}
          isPending={Boolean(
            isPending || (isSuccess && isEmailValid && !user?.email)
          )}
          onSubmit={() => {
            const submitValue = stepServicesData as StepService;
            delete submitValue?.participants; // remove unnecessary value for the submit

            // Extract division IDs from serviceDivisionGroups
            const divisionIds =
              submitValue?.serviceDivisionGroups
                ?.map((division) => division.id)
                .filter((id): id is string => id !== undefined) || undefined;
            delete submitValue?.serviceDivisionGroups; // remove unnecessary value for the submit

            mutate({
              ...submitValue,
              service: submitValue?.service?.id,
              serviceDivisionGroups: divisionIds
            });
          }}
        />
      )}
      <RegisteredUserModal
        isOpenAlertDialog={isOpenAlertDialog}
        setIsOpenAlertDialog={setIsOpenAlertDialog}
        organization={organization}
        email={emailHint}
      />
      {renderForm()}
    </>
  );
};

export default ParticipantsDataStep;
